import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import classNames from 'classnames';
import { Form as FinalForm } from 'react-final-form';

import { injectIntl, intlShape } from '../../../util/reactIntl';
import { Form, PrimaryButton } from '../../../components';
import { TUTULIST_LOWERCASE } from '../../../util/constants';
import getCountryCodes from '../../../translations/countryCodes';
import { useConfiguration } from '../../../context/configurationContext';

import ShippingAddress from './ShippingAddress';
import RenderNonUsOptions from './RenderNonUsOptions';
import DeliveryOptions from './DeliveryOptions';
import TutulistOptionBlock from './TutulistOptionBlock';

import '../../../styles/PhoneNumberInputGlobal.css';
import css from './ShippingDetailsForm.module.css';

const weightOptions = [
  { key: 'g', label: 'g' },
  { key: 'kg', label: 'kg' },
  { key: 'oz', label: 'oz' },
  { key: 'lb', label: 'lb' },
];

const ShippingDetailsFormComponent = ({
  rootClassName = null,
  className = null,
  formId = null,
  inProgress = false,
  ...props
}) => {
  const config = useConfiguration();
  const countryCodes = getCountryCodes(config.localization.locale);
  const [defaultWeightValues, setDefaultWeightValues] = useState({
    weight: 0,
    massUnit: 'oz',
  });

  return (
    <FinalForm
      rootClassName={rootClassName}
      className={className}
      formId={formId}
      inProgress={inProgress}
      {...props}
      render={fieldRenderProps => {
        const {
          className,
          rootClassName,
          disabled,
          handleSubmit,
          intl,
          formId,
          invalid,
          reviewSent,
          values,
          form,
          currentUser,
          inProgress,
          TTlWeightWrapper = false,
          saveWeightInProgress = false,
          isShippingDetailsPage = false,
          onSaveDefaultToAllListings = () => {},
          onManageDisableScrolling = () => {},
          onRefreshCurrentUser = () => {},
          onSaveVerifiedPhoneNumber = () => {},
          onVerifyShippingPreferences = () => {},
        } = fieldRenderProps;

        const { shippingPreference } = values || {};
        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const submitDisabled = invalid || disabled || submitInProgress || !shippingPreference;
        const isTutulist = shippingPreference === TUTULIST_LOWERCASE;

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <div className={css.contactDetailsSection}>
              <div className={css.sectionContainer}>
                <DeliveryOptions
                  form={form}
                  intl={intl}
                  onVerifyShippingPreferences={value => onVerifyShippingPreferences(value)}
                />

                {shippingPreference === TUTULIST_LOWERCASE ? (
                  <ShippingAddress
                    currentUser={currentUser}
                    intl={intl}
                    form={form}
                    values={values}
                    formId={formId}
                    countryCodes={countryCodes}
                    isTutulist={isTutulist}
                    TTlWeightWrapper={TTlWeightWrapper}
                    onManageDisableScrolling={onManageDisableScrolling}
                    onRefreshCurrentUser={onRefreshCurrentUser}
                    onSaveVerifiedPhoneNumber={onSaveVerifiedPhoneNumber}
                  />
                ) : (
                  <RenderNonUsOptions intl={intl} />
                )}

                <TutulistOptionBlock
                  intl={intl}
                  defaultWeightValues={defaultWeightValues}
                  setDefaultWeightValues={setDefaultWeightValues}
                  saveWeightInProgress={saveWeightInProgress}
                  onSaveDefaultToAllListings={onSaveDefaultToAllListings}
                  weightOptions={weightOptions}
                  showSetDefaultWeight={true}
                  defaultWeightLabelMessage={intl.formatMessage({
                    id: 'ShippingDetailsForm.defaultWeight',
                  })}
                  defaultWeightRequiredMessage={intl.formatMessage({
                    id: 'ShippingDetailsForm.defaultWeightRequired',
                  })}
                  isShippingDetailsPage={isShippingDetailsPage}
                />
                <div className={css.bottomWrapper}>
                  <PrimaryButton
                    className={css.submitButton}
                    type="submit"
                    inProgress={submitInProgress}
                    disabled={submitDisabled}
                    ready={reviewSent}
                  >
                    {intl.formatMessage({
                      id: 'ShippingDetailsForm.saveDetails',
                    })}
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </Form>
        );
      }}
    />
  );
};

const { bool, func, string } = PropTypes;

ShippingDetailsFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  formId: string,
  inProgress: bool,
  onSaveVerifiedPhoneNumber: func,
  intl: intlShape.isRequired,
  ready: bool.isRequired,
};

const ShippingDetailsForm = compose(injectIntl)(ShippingDetailsFormComponent);

ShippingDetailsForm.displayName = 'ShippingDetailsForm';

export default ShippingDetailsForm;
